import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  HouseLine, Drop, Receipt, FileText, ChatCircleText, ListChecks,
  Coins, Bank, Stack, Gear, SignOut, Check, CaretUpDown, Sparkle, Tray, Sun, Scales, Archive, ClockCountdown,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const PRIMARY_ITEMS = [
  { to: "/morning-check", label: "Morning Check", icon: Sun, testid: "nav-morning-check" },
];
const PORTFOLIO_ITEMS = [
  { to: "/real-estate",    label: "Real Estate",    icon: HouseLine, testid: "nav-real-estate",     assetType: "real_estate" },
  { to: "/oil-gas",        label: "Oil & Gas",      icon: Drop,      testid: "nav-oil-gas",          assetType: "mineral_royalty" },
  { to: "/mortgage-notes", label: "Mortgage Notes", icon: Receipt,   testid: "nav-mortgage-notes",   assetType: "note_receivable" },
  { to: "/private-funds",  label: "Private Funds",  icon: Stack,     testid: "nav-private-funds",    assetType: "private_equity_lp" },
  { to: "/precious-metals",label: "Precious Metals",icon: Coins,     testid: "nav-precious-metals",  assetType: "precious_metals" },
];
const LIABILITIES_ITEM = { to: "/liabilities", label: "Liabilities", icon: Scales, testid: "nav-liabilities" };
const OPERATIONS_ITEMS = [
  { to: "/transactions", label: "Transactions", icon: Bank,           testid: "nav-transactions" },
  { to: "/issues",       label: "Issues",       icon: ListChecks,     testid: "nav-issues" },
  { to: "/obligations",  label: "Obligations",  icon: ClockCountdown, testid: "nav-obligations" },
  { to: "/ask-max",      label: "Ask Max",      icon: ChatCircleText, testid: "nav-ask-max" },
  { to: "/inbox",        label: "Inbox",        icon: Tray,           testid: "nav-ingest" },
  { to: "/archive",      label: "Archive",      icon: Archive,        testid: "nav-archive" },
  { to: "/comms/audit",  label: "Comms Audit",  icon: FileText,       testid: "nav-comms-audit" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { households, active, setActiveId, assetProblemIds, assets, liabilities, liabilityDueIds, ownerLabels, ownerLabel } = useHousehold();
  const navigate = useNavigate();

  // Modular nav: a portfolio category only appears once the household actually holds
  // something in it. Routes stay live either way, so deep links keep working.
  const portfolioItems = PORTFOLIO_ITEMS.filter((it) =>
    (assets || []).some((a) => a.asset_type === it.assetType)
  );
  const showLiabilities = (liabilities || []).length > 0;

  // Per-portfolio-item alert dot — derive from existing problem maps.
  const itemAlertCount = (it) => {
    if (!it.assetType) return 0;
    if (it.assetType === "real_estate") {
      // Real-estate problems: delinquent tax or lease expiring (assetProblemIds includes both)
      return (assets || []).filter((a) => a.asset_type === "real_estate" && assetProblemIds?.has(a.id)).length;
    }
    return (assets || []).filter((a) => a.asset_type === it.assetType && (a.review_status === "needs_review")).length;
  };

  const renderItem = (n) => {
    const alerts = itemAlertCount(n);
    return (
      <NavLink
        key={n.to}
        to={n.to}
        data-testid={n.testid}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium surface-hover ${
            isActive
              ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
              : "text-[hsl(var(--sidebar-foreground))]/75 hover:bg-[hsl(218_40%_22%)] hover:text-[hsl(var(--sidebar-foreground))]"
          }`
        }
      >
        <n.icon size={17} weight="regular" className="opacity-80 group-hover:opacity-100 shrink-0" />
        <span className="truncate">{n.label}</span>
        {alerts > 0 && (
          <span
            data-testid={`${n.testid}-alert-dot`}
            className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-destructive/15 text-destructive text-[10px] font-bold px-1"
          >
            {alerts}
          </span>
        )}
      </NavLink>
    );
  };

  // User card initials — try operator A/B labels first, fallback to user name.
  const labelA = ownerLabels?.A || "A";
  const labelB = ownerLabels?.B || "B";
  const initA = (labelA[0] || "A").toUpperCase();
  const initB = (labelB[0] || "B").toUpperCase();
  const householdLabel = active?.role === "operator" ? "Joint household" : (active?.role || "Member");

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-[232px] bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] sticky top-0 h-screen border-r border-[hsl(218_40%_22%)]">
        {/* Brand */}
        <div className="h-16 flex items-center gap-2.5 px-5">
          <div className="h-8 w-8 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center shadow-sm" data-testid="brand-monogram">
            <span className="font-serif text-base font-semibold leading-none mt-[1px]">O</span>
          </div>
          <span className="brand-wordmark text-[17px] text-[hsl(var(--sidebar-foreground))]" data-testid="brand-wordmark">OurHomebase</span>
        </div>

        {/* Household switcher (optional, only if >1) */}
        {households.length > 1 && active && (
          <div className="px-3 pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="household-switcher" className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md bg-[hsl(218_40%_22%)] hover:bg-[hsl(218_40%_28%)] text-[hsl(var(--sidebar-foreground))] text-xs font-semibold transition-colors">
                <span className="truncate">{active.name}</span>
                <CaretUpDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-52">
                {households.map((h) => (
                  <DropdownMenuItem key={h.id} onClick={() => setActiveId(h.id)} className="flex items-center justify-between">
                    <span className="truncate">{h.name}</span>
                    {h.id === active.id && <Check size={14} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-3 py-2 space-y-6 overflow-y-auto">
          <div className="space-y-0.5">
            {PRIMARY_ITEMS.map(renderItem)}
          </div>
          {portfolioItems.length > 0 && (
            <div className="space-y-0.5">
              <p className="eyebrow px-3 mb-2 !text-[hsl(var(--sidebar-foreground))]/50">Portfolio</p>
              {portfolioItems.map(renderItem)}
            </div>
          )}
          {showLiabilities && (
            <div className="space-y-0.5">
              {renderItem(LIABILITIES_ITEM)}
            </div>
          )}
          <div className="space-y-0.5">
            <p className="eyebrow px-3 mb-2 !text-[hsl(var(--sidebar-foreground))]/50">Operations</p>
            {OPERATIONS_ITEMS.map(renderItem)}
          </div>
        </nav>

        {/* User card (bottom) */}
        <DropdownMenu>
          <DropdownMenuTrigger data-testid="user-card-trigger" className="m-3 flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-[hsl(218_40%_22%)] text-left outline-none surface-hover text-[hsl(var(--sidebar-foreground))]">
            <div className="flex -space-x-1.5 shrink-0">
              <div className="h-7 w-7 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center text-[11px] font-bold ring-2 ring-[hsl(var(--sidebar))]">
                {initA}
              </div>
              <div className="h-7 w-7 rounded-full bg-[hsl(218_40%_30%)] text-[hsl(var(--sidebar-foreground))] flex items-center justify-center text-[11px] font-bold ring-2 ring-[hsl(var(--sidebar))]">
                {initB}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold leading-tight truncate">{labelA} &amp; {labelB}</p>
              <p className="text-[11px] text-[hsl(var(--sidebar-foreground))]/60 leading-tight mt-0.5 flex items-center gap-1">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--success))]" />
                {householdLabel}
              </p>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-52">
            <DropdownMenuItem data-testid="nav-settings-menu" onClick={() => navigate("/settings")}>
              <Gear size={14} className="mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-household-menu" onClick={() => navigate("/household")}>
              <Sparkle size={14} className="mr-2" /> Household
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-button" onClick={logout}>
              <SignOut size={14} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top nav */}
        <header className="md:hidden h-14 border-b border-[hsl(218_40%_22%)] bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))] sticky top-0 z-30 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] flex items-center justify-center">
              <span className="font-serif text-sm font-semibold">O</span>
            </div>
            <span className="brand-wordmark text-sm text-[hsl(var(--sidebar-foreground))]">OurHomebase</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="mobile-menu-trigger" className="px-3 py-1 text-xs font-semibold rounded-md bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">Menu</DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {[...PRIMARY_ITEMS, ...portfolioItems, ...(showLiabilities ? [LIABILITIES_ITEM] : []), ...OPERATIONS_ITEMS].map((n) => (
                <DropdownMenuItem key={n.to} onClick={() => navigate(n.to)}>
                  <n.icon size={14} className="mr-2" /> {n.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")}><Gear size={14} className="mr-2" /> Settings</DropdownMenuItem>
              <DropdownMenuItem onClick={logout}><SignOut size={14} className="mr-2" /> Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 px-6 sm:px-10 lg:px-14 py-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
